import React, { useEffect, useState } from 'react';
import { formatDisplayDate } from '../../services/dateUtils';
import HelpAsk from '../../help/HelpAsk';
import { HelpOffer } from '../../help/HelpOffer';

// Shared by AttendanceConfirmModal so the dashboard's "Cancel session
// (one-off)" flow offers the exact same reasons as the Weekly Schedule.
export const CANCELLATION_REASON_PRESETS = [
  'Bank holiday',
  'School closed',
  'Sickness',
  'Trip / outing',
  'Weather',
  'Staff unavailable',
  'Other',
];

const PRESETS = CANCELLATION_REASON_PRESETS;

interface Props {
  open: boolean;
  /** Calendar date being cancelled (e.g. 2026-05-05). Null when in bulk
   *  mode — pass `count` instead and the title switches accordingly. */
  editDate: string | null;
  /** Number of sessions being cancelled — drives bulk vs single title copy. */
  count?: number;
  /** Inline error message rendered above the form. Used when the parent's
   *  attendance-block guard rejects the cancel — keeps the user in the
   *  modal so they see why their action stopped. */
  errorMessage?: string | null;
  /** A How To to offer beside that error — the way out of the block. */
  errorHelp?: HelpOffer | null;
  onConfirm: (reason: string | null) => void;
  onCancel: () => void;
}

const CancelDateModal: React.FC<Props> = ({ open, editDate, count = 1, errorMessage, errorHelp, onConfirm, onCancel }) => {
  const [preset, setPreset] = useState<string>('');
  const [freeText, setFreeText] = useState<string>('');

  useEffect(() => {
    if (open) {
      setPreset('');
      setFreeText('');
    }
  }, [open]);

  if (!open) return null;

  // Reason is mandatory: a preset must be picked, and if "Other" is chosen
  // the free-text field must be filled in.
  const free = freeText.trim();
  const isValid =
    (preset !== '' && preset !== 'Other') ||
    (preset === 'Other' && free.length > 0);

  const handleConfirm = () => {
    if (!isValid) return;
    let reason: string;
    if (preset === 'Other') {
      reason = free;
    } else {
      reason = free ? `${preset} — ${free}` : preset;
    }
    onConfirm(reason);
  };

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-date-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div
          className="modal-content ivory-panel"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-header" style={{ position: 'relative', zIndex: 1 }}>
            <h5
              id="cancel-date-title"
              className="modal-title d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <i className="fas fa-ban" style={{ color: '#a86a00', fontSize: '0.95rem' }} aria-hidden />
              {count > 1
                ? `Cancel ${count} sessions`
                : `Cancel session for ${editDate ? formatDisplayDate(editDate) : 'this date'}`}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onCancel}
            />
          </div>

          <div className="modal-body" style={{ position: 'relative', zIndex: 1 }}>
            {errorMessage && (
              <div
                role="alert"
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  borderRadius: 6,
                  background: 'rgba(184, 48, 47, 0.08)',
                  border: '1px solid rgba(184, 48, 47, 0.32)',
                  color: '#7a1f1d',
                  fontSize: '0.82rem',
                  lineHeight: 1.4,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <i className="fas fa-circle-exclamation" style={{ color: '#b8302f', marginTop: 2 }} aria-hidden />
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                  <span>{errorMessage}</span>
                  {errorHelp && <HelpAsk guideKey={errorHelp.guideKey} label={errorHelp.label} variant="inline" />}
                </span>
              </div>
            )}
            <label
              style={{
                fontSize: '0.72rem',
                color: '#5a6a58',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: 4,
              }}
            >
              Reason <span style={{ color: '#b8302f' }}>*</span>
            </label>
            <select
              className="form-select form-select-sm"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              style={{ marginBottom: 10 }}
              autoFocus
              required
            >
              <option value="">Select a reason…</option>
              {PRESETS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <textarea
              className="form-control form-control-sm"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                preset === 'Other'
                  ? 'Describe the reason (required)'
                  : 'Additional notes (optional)'
              }
              style={{ resize: 'vertical' }}
              required={preset === 'Other'}
            />
          </div>

          <div className="modal-footer" style={{ position: 'relative', zIndex: 1 }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {count > 1 ? 'Keep sessions' : 'Keep session'}
            </button>
            <button
              type="button"
              className="btn btn-warning d-inline-flex align-items-center gap-2"
              onClick={handleConfirm}
              disabled={!isValid}
            >
              <i className="fas fa-ban" aria-hidden />
              {count > 1 ? `Cancel ${count} sessions` : 'Cancel session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelDateModal;
